import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import NewsCard from '@/components/feature/NewsCard';
import EmptyState from '@/components/feature/EmptyState';
import { movies } from '@/mocks/movies';
import { newsList } from '@/mocks/news';

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = searchParams.get('q') || '';
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const { movieResults, newsResults } = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return { movieResults: [], newsResults: [] };
    const movieResults = movies.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.titleEn.toLowerCase().includes(q) ||
        m.category.includes(q) ||
        m.genres.some((g) => g.includes(q)) ||
        m.director.toLowerCase().includes(q) ||
        m.cast.some((c) => c.toLowerCase().includes(q)),
    );
    const newsResults = newsList.filter(
      (n) => n.title.toLowerCase().includes(q) || n.excerpt.toLowerCase().includes(q) || n.category.includes(q),
    );
    return { movieResults, newsResults };
  }, [keyword]);

  const hotMovies = [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, 6);
  const total = movieResults.length + newsResults.length;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="w-full bg-background-50">
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-screen mx-auto">
          <nav className="text-xs md:text-sm text-foreground-600 mb-6 flex items-center gap-1.5">
            <Link to="/" className="hover:text-primary-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">搜索结果</span>
          </nav>

          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 mb-6 max-w-2xl">
            <div className="flex-1 flex items-center gap-2 bg-background-100 border border-background-300/60 rounded-full px-4 py-2.5 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-foreground-500"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入电影名称、类型或资讯关键词"
                className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-500"
                aria-label="搜索关键词"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          {keyword ? (
            <>
              <p className="text-sm text-foreground-600 mb-8">
                关键词 “<span className="text-primary-400 font-semibold">{keyword}</span>” 共找到
                <span className="text-foreground-900 font-semibold"> {movieResults.length} </span>部影片、
                <span className="text-foreground-900 font-semibold"> {newsResults.length} </span>条资讯
              </p>

              {total === 0 ? (
                <div className="bg-background-100 border border-background-300/50 rounded-lg mb-10">
                  <EmptyState
                    icon="ri-search-line"
                    title="没有找到相关内容"
                    description={`未找到与 “${keyword}” 相关的电影或资讯，换一个关键词试试，或看看下方的热门推荐。`}
                    actionLabel="浏览全部影片"
                    onAction={() => navigate('/movies')}
                  />
                </div>
              ) : (
                <div className="space-y-12 mb-10">
                  {movieResults.length > 0 && (
                    <section>
                      <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                        <i className="ri-film-line text-primary-500"></i> 相关影片（{movieResults.length}）
                      </h2>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                        {movieResults.map((movie) => (
                          <MovieCard key={movie.id} movie={movie} />
                        ))}
                      </div>
                    </section>
                  )}

                  {newsResults.length > 0 && (
                    <section>
                      <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                        <i className="ri-newspaper-line text-primary-500"></i> 相关资讯（{newsResults.length}）
                      </h2>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
                        {newsResults.map((article) => (
                          <NewsCard key={article.id} article={article} />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="mb-10 p-6 bg-background-100 border border-background-300/50 rounded-lg">
              <p className="text-sm text-foreground-600">输入关键词开始搜索，支持电影名称、类型与资讯标题。</p>
            </div>
          )}

          <section className="pb-14">
            <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-fire-fill text-primary-500"></i> 热门内容推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}